import { companyInfo } from "@/mocks/homeData";
import { useState, useRef } from "react";

export default function InquirySection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const name = formData.get("name") as string;
    const tel = formData.get("tel") as string;
    const cont = formData.get("cont") as string;

    // (1) 필수값 검증
    if (!name || !tel || !cont) {
      alert("필수 항목을 입력해 주세요.");
      return;
    }

    // (2) 개인정보 동의 체크 확인
    if (!agreed) {
      alert("개인정보 수집 및 이용에 동의해 주세요.");
      return;
    }

    // (3) 버튼 텍스트 변경 및 비활성화
    setSubmitting(true);

    try {
      // (4) fetch POST 요청 — JSON body
      const res = await fetch(
        "https://kcontents.eemo.co.kr/api/moonwi_insert.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            tel,
            email: formData.get("email"),
            nation: formData.get("nation"),
            moon_type: formData.get("moon_type"),
            cont,
          }),
        }
      );

      const data = await res.json();

      if (data.success === true) {
        // (5) 성공
        alert("문의가 접수되었습니다. 빠른 시일 내에 연락 드리겠습니다.");
        form.reset();
        setAgreed(false);
      } else {
        // (6) 실패
        alert(data.msg || "접수에 실패했습니다.");
      }
    } catch {
      // (7) 오류
      alert("접수 중 오류가 발생했습니다. 전화(042-639-6660)로 문의해 주세요.");
    } finally {
      // (8) 복원
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="relative w-full bg-white py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-on-scroll text-center mb-10 md:mb-14">
            <p className="text-sky-500 text-sm font-semibold tracking-wider uppercase mb-3">
              Inquiry
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-navy-900">
              문의 접수
            </h2>
            <p className="text-navy-500 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              유학 상담, 교육 프로그램, 문화행사, 취업 연계 등 궁금한 사항을
              남겨주시면 빠르게 답변 드리겠습니다
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10">
            {/* Form */}
            <div className="animate-on-scroll delay-100 lg:col-span-3">
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="bg-navy-50 rounded-xl p-5 md:p-8 border border-navy-100"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">
                      이름 <span className="text-sky-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="이름을 입력해 주세요"
                      className="w-full px-4 py-2.5 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder:text-navy-300 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">
                      연락처 <span className="text-sky-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="tel"
                      required
                      placeholder="010-0000-0000"
                      className="w-full px-4 py-2.5 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder:text-navy-300 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">
                      이메일
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="example@email.com"
                      className="w-full px-4 py-2.5 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder:text-navy-300 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                    />
                  </div>

                  {/* Nationality */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">
                      국적
                    </label>
                    <select
                      name="nation"
                      className="w-full px-4 py-2.5 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors appearance-none"
                      style={{
                        backgroundImage:
                          "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22627598%22 stroke-width=%222%22><path d=%22m6 9 6 6 6-6%22/></svg>')",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                        backgroundSize: "16px 16px",
                      }}
                    >
                      <option value="">국적을 선택하세요</option>
                      <option value="한국">한국</option>
                      <option value="베트남">베트남</option>
                      <option value="필리핀">필리핀</option>
                      <option value="몽골">몽골</option>
                      <option value="중국">중국</option>
                      <option value="일본">일본</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>

                  {/* Inquiry Type */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">
                      문의 유형
                    </label>
                    <select
                      name="moon_type"
                      className="w-full px-4 py-2.5 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors appearance-none"
                      style={{
                        backgroundImage:
                          "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22627598%22 stroke-width=%222%22><path d=%22m6 9 6 6 6-6%22/></svg>')",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                        backgroundSize: "16px 16px",
                      }}
                    >
                      <option value="">문의 유형을 선택하세요</option>
                      <option value="유학 상담">유학 상담</option>
                      <option value="교육 프로그램">교육 프로그램</option>
                      <option value="취업 연계">취업 연계</option>
                      <option value="문화행사·축제">문화행사·축제</option>
                      <option value="스포츠 프로그램">스포츠 프로그램</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">
                      문의 내용 <span className="text-sky-500">*</span>
                    </label>
                    <textarea
                      name="cont"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="문의 내용을 입력해 주세요"
                      className="w-full px-4 py-2.5 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder:text-navy-300 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors resize-none"
                    />
                    <p className="text-xs text-navy-400 mt-1 text-right">
                      최대 500자
                    </p>
                  </div>
                </div>

                {/* Privacy Agreement */}
                <div className="mt-4 md:mt-5">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-sky-500 cursor-pointer"
                    />
                    <span className="text-sm text-navy-600 leading-relaxed">
                      개인정보 수집 및 이용에 동의합니다 (필수)
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-5 w-full sm:w-auto px-8 py-3 bg-sky-500 hover:bg-sky-400 disabled:bg-sky-300 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
                >
                  {submitting ? "접수 중..." : "문의 접수하기"}
                </button>
              </form>
            </div>

            {/* Info Card */}
            <div className="animate-on-scroll delay-200 lg:col-span-2">
              <div className="bg-navy-900 rounded-xl p-5 md:p-6 text-white h-full flex flex-col">
                <h3 className="text-base font-bold mb-5 flex items-center gap-2">
                  <i className="ri-information-line text-sky-300"></i>
                  문의 안내
                </h3>

                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-sky-300 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-0.5">영업시간</p>
                      <p className="text-sm font-medium">평일 09:00 ~ 18:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-sky-300 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-0.5">대표전화</p>
                      <a
                        href={`tel:${companyInfo.phone}`}
                        className="text-sm font-medium hover:text-sky-300 transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-sky-300 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-0.5">이메일</p>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="text-sm font-medium hover:text-sky-300 transition-colors break-all"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-sky-300 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-0.5">주소</p>
                      <p className="text-sm font-medium">
                        {companyInfo.address}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="text-xs text-white/40 leading-relaxed">
                    문의하신 내용은 영업시간 내 우선순위에 따라 답변 드리며,
                    평균 1~2영업일 내 회신됩니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}